import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Typography,
  Row,
  Col,
  Radio,
  Progress,
  Checkbox,
  Drawer,
  Descriptions,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DownloadOutlined,
  UploadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Text } = Typography;
const { TextArea } = Input;

const BASE_URL = 'https://ds2.cn/mp';
const API = '/api/v1/meme-pet';

interface BeastType {
  id: number;
  code: string;
  speciesId?: string;
  name: string;
  description: string;
  emoji?: string;
  level: string;
  beastClass?: string;
  beastType?: string;
  baseHp: number;
  baseAttack: number;
  baseDefense: number;
  baseSpeed: number;
  sleepDurationMinutes?: number;
  exploreDurationMinutes?: number;
  preferredTerrain?: string;
  terrainForest?: number;
  terrainSwamp?: number;
  terrainDesert?: number;
  terrainCoast?: number;
  terrainVolcanic?: number;
  satietyDrainMultiplier?: number;
  primaryFood?: string;
  bodySize?: string;
  textureBaby?: string;
  textureJuvenile?: string;
  textureAdult?: string;
  textureBeast?: string;
  talentRange?: string;
  terrainTalentRange?: string;
  combatLoot?: string;
  growthStages?: string;
  hatchPool?: string;
  created: string;
  updated: string;
  instanceCount?: number;
}

const RARITY_OPTIONS = ['UR', 'QR', 'SSR', 'SR'];
const TERRAIN_OPTIONS = [
  { value: 'forest', label: '森林' },
  { value: 'swamp', label: '沼泽' },
  { value: 'desert', label: '沙漠' },
  { value: 'volcanic', label: '火山' },
  { value: 'coast', label: '海岸' },
];

// 把 Excel 行映射为后端 DTO（与 build_imports.py 的列名严格对应）
function rowToBeastDto(row: any) {
  const get = (...keys: string[]) => {
    for (const k of keys) if (row[k] !== undefined && row[k] !== '') return row[k];
    return undefined;
  };
  const tryJson = (v: any) => {
    if (v === undefined || v === '' || v === null) return undefined;
    if (typeof v === 'object') return v;
    try { return JSON.parse(String(v)); } catch { return String(v); }
  };
  const toNum = (v: any) => (v === '' || v === undefined || v === null ? undefined : Number(v));

  // code 优先用 card_id；没有则用 species_id（解决 4 只孵化池物种没 card_id 的问题）
  const cardId = get('编号(card_id)', 'code');
  const speciesId = get('species_id');

  return {
    code: (cardId || speciesId || '').toString().trim(),
    speciesId,
    name: get('名称', 'name') || '',
    description: get('描述', 'description') || '',
    emoji: get('emoji'),
    level: get('稀有度', 'level') || 'SR',
    beastClass: get('类别(class)', 'beastClass') || 'beast',
    beastType: get('卵/蛋 标记(type)', 'beastType'),
    baseHp: toNum(get('基础血量', 'baseHp')) || 0,
    baseAttack: toNum(get('基础攻击', 'baseAttack')) || 0,
    baseDefense: toNum(get('基础防御', 'baseDefense')) || 0,
    baseSpeed: toNum(get('基础速度', 'baseSpeed')) || 0,
    sleepDurationMinutes: toNum(get('睡眠分钟')),
    exploreDurationMinutes: toNum(get('探索分钟')),
    preferredTerrain: get('偏好地形'),
    terrainForest: toNum(get('森林适应')),
    terrainSwamp: toNum(get('沼泽适应')),
    terrainDesert: toNum(get('沙漠适应')),
    terrainCoast: toNum(get('海岸适应')),
    terrainVolcanic: toNum(get('火山适应')),
    satietyDrainMultiplier: toNum(get('饱食流失倍率')),
    primaryFood: get('主食'),
    bodySize: get('体型'),
    textureBaby: get('图片-幼年(baby)'),
    textureJuvenile: get('图片-青年(juvenile)'),
    textureAdult: get('图片-成年(adult)'),
    textureBeast: get('图片-猛兽(beast)'),
    talentRange: tryJson(get('天赋范围JSON(talent_range)')),
    terrainTalentRange: tryJson(get('地形天赋范围JSON(terrain_talent_range)')),
    combatLoot: tryJson(get('战利品JSON(combat_loot)')),
    growthStages: tryJson(get('成长阶段JSON(growth_stages)')),
    hatchPool: tryJson(get('孵化池JSON(hatch_pool, 仅蛋)')),
  };
}

const BEAST_TEMPLATE_HEADERS = [
  '编号(card_id)', 'species_id', '名称', 'emoji', '类别(class)', '卵/蛋 标记(type)',
  '稀有度', '基础血量', '基础攻击', '基础防御', '基础速度',
  '睡眠分钟', '探索分钟', '偏好地形',
  '森林适应', '沼泽适应', '沙漠适应', '海岸适应', '火山适应',
  '饱食流失倍率', '主食', '体型',
  '图片-幼年(baby)', '图片-青年(juvenile)', '图片-成年(adult)', '图片-猛兽(beast)',
  '天赋范围JSON(talent_range)', '地形天赋范围JSON(terrain_talent_range)',
  '战利品JSON(combat_loot)', '成长阶段JSON(growth_stages)',
  '孵化池JSON(hatch_pool, 仅蛋)', '描述',
];

const BeastTab: React.FC = () => {
  const [types, setTypes] = useState<BeastType[]>([]);
  const [loading, setLoading] = useState(false);
  const [typeModalVisible, setTypeModalVisible] = useState(false);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [batchGenerateModalVisible, setBatchGenerateModalVisible] = useState(false);
  const [batchUploadModalVisible, setBatchUploadModalVisible] = useState(false);
  const [selectedType, setSelectedType] = useState<BeastType | null>(null);
  const [uploadedData, setUploadedData] = useState<any[]>([]);
  const [overwriteOnImport, setOverwriteOnImport] = useState<boolean>(false);

  // 详情抽屉
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailRecord, setDetailRecord] = useState<BeastType | null>(null);

  const [batchProgress, setBatchProgress] = useState({
    currentType: '',
    currentTypeIndex: 0,
    totalTypes: 0,
    status: 'idle' as 'idle' | 'running' | 'completed' | 'error',
    completedTypes: [] as string[],
    failedTypes: [] as { code: string; error: string }[],
  });

  const [typeForm] = Form.useForm();
  const [generateForm] = Form.useForm();
  const [batchGenerateForm] = Form.useForm();

  useEffect(() => { fetchTypes(); }, []);

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/types`);
      const data = await res.json();
      if (data.success) setTypes((data.data || []).filter((t: BeastType) => /^BC210-\d+$/.test(t.code)));
      else message.error(data.message || '获取Meme Pet种类失败');
    } catch { message.error('获取Meme Pet种类失败'); }
    finally { setLoading(false); }
  };

  const createType = async (values: any) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: values.code, name: values.name, description: values.description,
          level: values.level || 'SR',
          baseHp: values.baseHp || 0, baseAttack: values.baseAttack || 0,
          baseDefense: values.baseDefense || 0, baseSpeed: values.baseSpeed || 0,
          preferredTerrain: values.preferredTerrain || null,
          emoji: values.emoji || null,
          textureAdult: values.textureAdult || null,
          beastClass: values.beastClass || 'beast',
          beastType: values.beastType || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        message.success('创建成功');
        setTypeModalVisible(false);
        typeForm.resetFields();
        fetchTypes();
      } else { message.error(data.message || '创建失败'); }
    } catch { message.error('创建失败'); }
    finally { setLoading(false); }
  };

  const downloadTemplate = () => {
    const sample = [{
      [BEAST_TEMPLATE_HEADERS[0]]: 'BC210-002',
      [BEAST_TEMPLATE_HEADERS[1]]: 'herrerasaurus_001',
      [BEAST_TEMPLATE_HEADERS[2]]: '埃雷拉龙',
      [BEAST_TEMPLATE_HEADERS[3]]: '🦖',
      [BEAST_TEMPLATE_HEADERS[4]]: 'beast',
      [BEAST_TEMPLATE_HEADERS[5]]: '',
      [BEAST_TEMPLATE_HEADERS[6]]: 'UR',
      [BEAST_TEMPLATE_HEADERS[7]]: 250,
      [BEAST_TEMPLATE_HEADERS[8]]: 75,
      [BEAST_TEMPLATE_HEADERS[9]]: 15,
      [BEAST_TEMPLATE_HEADERS[10]]: 28,
      [BEAST_TEMPLATE_HEADERS[11]]: 6,
      [BEAST_TEMPLATE_HEADERS[12]]: 13,
      [BEAST_TEMPLATE_HEADERS[13]]: 'desert',
      [BEAST_TEMPLATE_HEADERS[14]]: 0.9,
      [BEAST_TEMPLATE_HEADERS[15]]: 0.8,
      [BEAST_TEMPLATE_HEADERS[16]]: 1.3,
      [BEAST_TEMPLATE_HEADERS[17]]: 0.5,
      [BEAST_TEMPLATE_HEADERS[18]]: 0.7,
      [BEAST_TEMPLATE_HEADERS[19]]: 1.8,
      [BEAST_TEMPLATE_HEADERS[20]]: '小型恐龙、爬行动物',
      [BEAST_TEMPLATE_HEADERS[21]]: '3-6米',
      [BEAST_TEMPLATE_HEADERS[22]]: '/static/textures/species/002-1.png',
      [BEAST_TEMPLATE_HEADERS[23]]: '/static/textures/species/002-2.png',
      [BEAST_TEMPLATE_HEADERS[24]]: '/static/textures/species/002-3.png',
      [BEAST_TEMPLATE_HEADERS[25]]: '/static/textures/species/002-4.png',
      [BEAST_TEMPLATE_HEADERS[26]]: '{"hp_bonus":[0,67],"attack_bonus":[0,26]}',
      [BEAST_TEMPLATE_HEADERS[27]]: '{"desert":[0,0.4]}',
      [BEAST_TEMPLATE_HEADERS[28]]: '{"meat":{"item_id":"meat_dragon","drop_probability":0.8,"max_quantity":12}}',
      [BEAST_TEMPLATE_HEADERS[29]]: '{"baby":{"stat_coefficient":0.38}}',
      [BEAST_TEMPLATE_HEADERS[30]]: '',
      [BEAST_TEMPLATE_HEADERS[31]]: '三叠纪最凶猛的肉食恐龙之一...',
    }];
    const ws = XLSX.utils.json_to_sheet(sample, { header: BEAST_TEMPLATE_HEADERS });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Meme Pet');
    XLSX.writeFile(wb, 'Meme Pet-种类导入模板.xlsx');
    message.success('模板下载成功');
  };

  const handleFileUpload = (file: File) => {
    const isJson = file.name.toLowerCase().endsWith('.json');
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let rows: any[] = [];
        if (isJson) {
          // ⚡ 推荐路径 —— JSON 原生读取，UTF-8 emoji 零损耗
          rows = JSON.parse(e.target?.result as string);
        } else {
          // xlsx 路径：SheetJS 0.18.5 对 4 字节 emoji 有 bug，会把 SMP 平面字符
          // 截成 BMP（U+1F96C 🥬 → U+F96C "塞"）。建议改用同目录下的 .json
          const wb = XLSX.read(e.target?.result, { type: 'binary' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          rows = XLSX.utils.sheet_to_json(ws);
          // 警告：检测到疑似被截断的 emoji（U+F000 ~ U+FFFD 范围内的孤字）
          const suspicious = rows.some((r: any) =>
            typeof r.emoji === 'string' && r.emoji.length === 1 &&
            r.emoji.charCodeAt(0) >= 0xF000 && r.emoji.charCodeAt(0) <= 0xFFFD,
          );
          if (suspicious) {
            message.warning(
              '检测到 xlsx 里 emoji 字段疑似被截断（SheetJS 已知 bug）。' +
              '请改用同目录下的 .json 文件上传以避免乱码。',
              8,
            );
          }
        }
        const valid = rows.map((r: any) => rowToBeastDto(r)).filter(d => d.code && d.name);
        setUploadedData(valid);
        message.success(`成功读取 ${valid.length} 条数据${isJson ? '（JSON）' : '（XLSX）'}`);
      } catch (err) { message.error('文件解析失败'); console.error(err); }
    };
    if (isJson) reader.readAsText(file, 'utf-8');
    else reader.readAsBinaryString(file);
    return false;
  };

  const batchCreateTypes = async () => {
    if (uploadedData.length === 0) { message.warning('请先上传文件'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/types/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: uploadedData, overwrite: overwriteOnImport }),
      });
      const data = await res.json();
      if (data.success) {
        const r = data.data || {};
        const failedList = (r.failed || []).slice(0, 8).map((f: any) => `${f.code}: ${f.error}`).join('\n');
        message.success(
          `批量导入完成！创建 ${r.created}，更新 ${r.updated}，跳过 ${r.skipped}，失败 ${r.failed?.length || 0}`,
          5,
        );
        if (failedList) {
          Modal.warning({
            title: '部分条目失败',
            content: <pre style={{ maxHeight: 300, overflow: 'auto' }}>{failedList}</pre>,
            width: 600,
          });
        }
      } else {
        message.error(data.message || '批量导入失败');
      }
    } catch (err) {
      console.error(err);
      message.error('批量导入失败');
    } finally {
      setLoading(false);
      setBatchUploadModalVisible(false);
      setUploadedData([]);
      fetchTypes();
    }
  };

  const generateInstances = async (values: any) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/types/${selectedType?.id}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: values.quantity }),
      });
      const data = await res.json();
      if (data.success) {
        message.success(`成功生成 ${values.quantity} 个实例`);
        const instances = data.data.instances as { hash: string }[];
        const content = instances.map(i => `${BASE_URL}/${i.hash}`).join('\r\n') + '\r\n';
        const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
        saveAs(blob, `${selectedType?.code}.txt`);
        setGenerateModalVisible(false);
        generateForm.resetFields();
        fetchTypes();
      } else { message.error(data.message || '生成失败'); }
    } catch { message.error('生成失败'); }
    finally { setLoading(false); }
  };

  const fetchWithRetry = async (url: string, options: RequestInit, maxRetries = 3, retryDelay = 1000): Promise<Response> => {
    let lastError: Error | null = null;
    for (let i = 0; i <= maxRetries; i++) {
      try {
        const res = await fetch(url, options);
        if (res.ok) return res;
        if (res.status >= 400 && res.status < 500) return res;
        throw new Error(`Server error: ${res.status}`);
      } catch (err: any) {
        lastError = err;
        if (i === maxRetries) break;
        await new Promise(r => setTimeout(r, retryDelay * Math.pow(2, i)));
      }
    }
    throw lastError || new Error('请求失败');
  };

  const downloadAsZip = async (files: { code: string; content: string }[]) => {
    try {
      setLoading(true);
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      files.forEach(f => zip.file(`${f.code}.txt`, `﻿${f.content}`, { binary: false }));
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `ancient_beast_batch_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.zip`);
      message.success(`成功生成ZIP文件包含 ${files.length} 个文件`);
    } catch (err) { message.error('生成ZIP文件失败'); console.error(err); }
    finally { setLoading(false); }
  };

  const batchGenerateInstances = async (values: any) => {
    const { quantity, downloadType } = values;
    setBatchProgress({ currentType: '', currentTypeIndex: 0, totalTypes: types.length, status: 'running', completedTypes: [], failedTypes: [] });
    setLoading(true);
    try {
      const allFiles: { code: string; content: string }[] = [];
      for (let i = 0; i < types.length; i++) {
        const t = types[i];
        setBatchProgress(p => ({ ...p, currentType: t.code, currentTypeIndex: i + 1 }));
        try {
          const res = await fetchWithRetry(`${API}/types/${t.id}/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ quantity }),
          }, 3, 1000);
          const data = await res.json();
          if (data.success && data.data?.instances) {
            const content = data.data.instances.map((i: any) => `${BASE_URL}/${i.hash}`).join('\r\n') + '\r\n';
            allFiles.push({ code: t.code, content });
            setBatchProgress(p => ({ ...p, completedTypes: [...p.completedTypes, t.code] }));
            if (downloadType === 'batch') {
              const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
              saveAs(new Blob([BOM, content], { type: 'text/plain;charset=utf-8' }), `${t.code}.txt`);
              await new Promise(r => setTimeout(r, 500));
            }
          } else { throw new Error(data.message || '生成失败'); }
        } catch (err: any) {
          setBatchProgress(p => ({ ...p, failedTypes: [...p.failedTypes, { code: t.code, error: err.message || '未知错误' }] }));
        }
      }
      if (downloadType === 'zip' && allFiles.length > 0) await downloadAsZip(allFiles);
      setBatchProgress(p => ({ ...p, status: 'completed' }));
      const successCount = allFiles.length;
      message.success(`批量生成完成：成功 ${successCount} 个`);
      fetchTypes();
    } catch (err) { message.error('批量生成失败'); setBatchProgress(p => ({ ...p, status: 'error' })); }
    finally { setLoading(false); }
  };

  const renderLevel = (v: string) => {
    const colors: Record<string, string> = { UR: 'red', SSR: 'gold', SR: 'purple', QR: 'default' };
    return <Tag color={colors[v] || 'default'}>{v}</Tag>;
  };

  const fmtNum = (v?: number) => (v === null || v === undefined ? '—' : v);

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60, fixed: 'left' as const },
    { title: '编号', dataIndex: 'code', key: 'code', width: 130, fixed: 'left' as const },
    { title: 'species_id', dataIndex: 'speciesId', key: 'speciesId', width: 160 },
    { title: '名称', dataIndex: 'name', key: 'name', width: 110, fixed: 'left' as const },
    { title: 'emoji', dataIndex: 'emoji', key: 'emoji', width: 60 },
    {
      title: '稀有度', dataIndex: 'level', key: 'level', width: 80,
      render: renderLevel,
    },
    { title: '类别', dataIndex: 'beastClass', key: 'beastClass', width: 70 },
    { title: 'type', dataIndex: 'beastType', key: 'beastType', width: 70 },
    { title: 'HP', dataIndex: 'baseHp', key: 'baseHp', width: 60 },
    { title: 'ATK', dataIndex: 'baseAttack', key: 'baseAttack', width: 60 },
    { title: 'DEF', dataIndex: 'baseDefense', key: 'baseDefense', width: 60 },
    { title: 'SPD', dataIndex: 'baseSpeed', key: 'baseSpeed', width: 60 },
    { title: '睡眠', dataIndex: 'sleepDurationMinutes', key: 'sleep', width: 70, render: fmtNum },
    { title: '探索', dataIndex: 'exploreDurationMinutes', key: 'explore', width: 70, render: fmtNum },
    { title: '偏好地形', dataIndex: 'preferredTerrain', key: 'preferredTerrain', width: 90 },
    { title: '森林', dataIndex: 'terrainForest', key: 'tF', width: 60, render: fmtNum },
    { title: '沼泽', dataIndex: 'terrainSwamp', key: 'tS', width: 60, render: fmtNum },
    { title: '沙漠', dataIndex: 'terrainDesert', key: 'tD', width: 60, render: fmtNum },
    { title: '海岸', dataIndex: 'terrainCoast', key: 'tC', width: 60, render: fmtNum },
    { title: '火山', dataIndex: 'terrainVolcanic', key: 'tV', width: 60, render: fmtNum },
    { title: '主食', dataIndex: 'primaryFood', key: 'primaryFood', width: 140, ellipsis: true },
    { title: '体型', dataIndex: 'bodySize', key: 'bodySize', width: 90 },
    {
      title: '实例数', dataIndex: 'instanceCount', key: 'instanceCount', width: 80,
      render: (n: number) => n || 0,
    },
    {
      title: '操作', key: 'action', width: 150, fixed: 'right' as const,
      render: (_: any, record: BeastType) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />}
            onClick={() => { setDetailRecord(record); setDetailVisible(true); }}>
            详情
          </Button>
          <Button type="link" size="small"
            onClick={() => { setSelectedType(record); setGenerateModalVisible(true); }}>
            生成实例
          </Button>
        </Space>
      ),
    },
  ];

  // 美化 JSON 显示
  const renderJson = (s?: string | null) => {
    if (!s) return <Text type="secondary">—</Text>;
    try {
      const obj = JSON.parse(s);
      return <pre style={{ margin: 0, fontSize: 12, maxHeight: 200, overflow: 'auto' }}>{JSON.stringify(obj, null, 2)}</pre>;
    } catch {
      return <pre style={{ margin: 0, fontSize: 12 }}>{s}</pre>;
    }
  };

  return (
    <Card
      bordered={false}
      extra={
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setTypeModalVisible(true)}>创建新种类</Button>
          <Button icon={<UploadOutlined />} onClick={() => setBatchUploadModalVisible(true)}>批量导入</Button>
          <Button onClick={() => setBatchGenerateModalVisible(true)}>批量生成</Button>
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>下载模板</Button>
          <Button icon={<ReloadOutlined />} onClick={fetchTypes}>刷新</Button>
        </Space>
      }
    >
      <Table columns={columns} dataSource={types} rowKey="id" loading={loading} pagination={{ pageSize: 10 }} scroll={{ x: 2400 }} />

      {/* 创建种类 Modal —— 简化字段，复杂字段建议用 Excel 导入 */}
      <Modal title="创建Meme Pet种类" open={typeModalVisible} onOk={() => typeForm.submit()}
        onCancel={() => { setTypeModalVisible(false); typeForm.resetFields(); }} width={700}>
        <Form form={typeForm} layout="vertical" onFinish={createType}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="code" label="编号(card_id)" rules={[{ required: true }]}><Input placeholder="BC210-002" /></Form.Item></Col>
            <Col span={12}><Form.Item name="name" label="名称" rules={[{ required: true }]}><Input placeholder="埃雷拉龙" /></Form.Item></Col>
          </Row>
          <Form.Item name="description" label="描述" rules={[{ required: true }]}>
            <TextArea rows={3} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={6}><Form.Item name="level" label="稀有度" initialValue="SR" rules={[{ required: true }]}>
              <Select>{RARITY_OPTIONS.map(r => <Select.Option key={r} value={r}>{r}</Select.Option>)}</Select>
            </Form.Item></Col>
            <Col span={6}><Form.Item name="emoji" label="emoji"><Input placeholder="🦖" /></Form.Item></Col>
            <Col span={6}><Form.Item name="beastClass" label="类别" initialValue="beast">
              <Select><Select.Option value="beast">beast</Select.Option><Select.Option value="egg">egg</Select.Option></Select>
            </Form.Item></Col>
            <Col span={6}><Form.Item name="preferredTerrain" label="偏好地形">
              <Select allowClear>{TERRAIN_OPTIONS.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}</Select>
            </Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}><Form.Item name="baseHp" label="基础HP" initialValue={0}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="baseAttack" label="基础攻击" initialValue={0}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="baseDefense" label="基础防御" initialValue={0}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="baseSpeed" label="基础速度" initialValue={0}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="textureAdult" label="主图(textureAdult)"><Input placeholder="/static/textures/species/002-3.png" /></Form.Item>
          <Text type="secondary">复杂字段（成长阶段 / 战利品 / 地形天赋）建议通过 Excel 批量导入</Text>
        </Form>
      </Modal>

      {/* 生成实例 Modal */}
      <Modal title={`生成实例 - ${selectedType?.name}`} open={generateModalVisible}
        onOk={() => generateForm.submit()}
        onCancel={() => { setGenerateModalVisible(false); generateForm.resetFields(); }}>
        <Form form={generateForm} layout="vertical" onFinish={generateInstances}>
          <Form.Item name="quantity" label="生成数量" initialValue={100} rules={[{ required: true }]}>
            <InputNumber min={1} max={10000} style={{ width: '100%' }} />
          </Form.Item>
          <Text type="secondary">生成后下载 txt（{BASE_URL}/&lt;hash&gt;）</Text>
        </Form>
      </Modal>

      {/* 批量导入 Modal */}
      <Modal title="批量导入Meme Pet种类" open={batchUploadModalVisible}
        onOk={batchCreateTypes} okText={overwriteOnImport ? '确认导入（覆盖已存在）' : '确认导入'} cancelText="取消"
        onCancel={() => { setBatchUploadModalVisible(false); setUploadedData([]); }} width={900}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text>请上传 Excel 文件（与「下载模板」按钮一致）。</Text>
            <br />
            <Text type="secondary">推荐使用本仓库的 <code>Ancient Beast/imports/Meme Pet-种类导入.xlsx</code></Text>
          </div>
          <Checkbox
            checked={overwriteOnImport}
            onChange={e => setOverwriteOnImport(e.target.checked)}
          >
            <Text strong>覆盖已存在的条目（按编号 code 匹配，UPDATE 已有的）</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              不勾选时遇到重复编号会跳过；勾选则用新数据 UPDATE 已存在的行
            </Text>
          </Checkbox>
          <input type="file" accept=".xlsx,.xls,.json"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }}
            style={{ padding: 8, border: '1px dashed #d9d9d9', borderRadius: 4, width: '100%', cursor: 'pointer' }} />
          <Text type="warning" style={{ fontSize: 12 }}>
            ⚠️ 上传含 emoji 的数据时，推荐使用 <code>.json</code> 文件（xlsx 解析库对 emoji 有截断 bug）。
            两个文件都在 <code>Ancient Beast/imports/</code> 下。
          </Text>
          {uploadedData.length > 0 && (
            <div>
              <Text strong>已读取 {uploadedData.length} 条数据：</Text>
              <Table dataSource={uploadedData} size="small" pagination={false} scroll={{ y: 300, x: 800 }}
                rowKey={(_, i) => String(i || 0)}
                columns={[
                  { title: '编号', dataIndex: 'code', width: 110 },
                  { title: 'species_id', dataIndex: 'speciesId', width: 150 },
                  { title: '名称', dataIndex: 'name', width: 110 },
                  { title: '稀有度', dataIndex: 'level', width: 70 },
                  { title: '类别', dataIndex: 'beastClass', width: 70 },
                  { title: 'HP', dataIndex: 'baseHp', width: 60 },
                  { title: 'ATK', dataIndex: 'baseAttack', width: 60 },
                  { title: 'DEF', dataIndex: 'baseDefense', width: 60 },
                  { title: 'SPD', dataIndex: 'baseSpeed', width: 60 },
                ]} />
            </div>
          )}
        </Space>
      </Modal>

      {/* 批量生成 Modal */}
      <Modal title="批量生成Meme Pet实例" open={batchGenerateModalVisible} footer={null} width={600}
        onCancel={() => { setBatchGenerateModalVisible(false); batchGenerateForm.resetFields(); }}>
        <Form form={batchGenerateForm} layout="vertical" onFinish={batchGenerateInstances}>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">将为所有种类（共 {types.length} 个）各生成指定数量的实例</Text>
          </div>
          <Form.Item name="quantity" label="每种生成数量" rules={[{ required: true }]}>
            <InputNumber min={1} max={10000} style={{ width: '100%' }} disabled={loading} />
          </Form.Item>
          <Form.Item name="downloadType" label="下载方式" initialValue="batch" rules={[{ required: true }]}>
            <Radio.Group disabled={loading}>
              <Radio value="batch">即时下载</Radio>
              <Radio value="zip">ZIP 打包</Radio>
            </Radio.Group>
          </Form.Item>
          {batchProgress.status !== 'idle' && (
            <div style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
              <Text strong>生成进度</Text>
              <div style={{ marginBottom: 8 }}>
                {batchProgress.status === 'running' && <Text>正在处理: {batchProgress.currentType}</Text>}
                {batchProgress.status === 'completed' && <Text>✓ 完成</Text>}
                {batchProgress.status === 'error' && <Text type="danger">✗ 出错</Text>}
              </div>
              <Progress percent={batchProgress.totalTypes > 0 ? Math.round((batchProgress.currentTypeIndex / batchProgress.totalTypes) * 100) : 0}
                status={batchProgress.status === 'completed' ? 'success' : batchProgress.status === 'error' ? 'exception' : 'active'}
                format={() => `${batchProgress.currentTypeIndex}/${batchProgress.totalTypes}`} />
              <div style={{ marginTop: 12, fontSize: 12 }}>
                <Text type="success">已完成: {batchProgress.completedTypes.length} 个</Text>
                {batchProgress.failedTypes.length > 0 && (
                  <><br /><Text type="danger">失败: {batchProgress.failedTypes.length} 个</Text></>
                )}
              </div>
            </div>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading} disabled={batchProgress.status === 'running'} icon={<DownloadOutlined />}>
                {loading ? '生成中...' : '开始批量生成'}
              </Button>
              <Button onClick={() => { setBatchGenerateModalVisible(false); batchGenerateForm.resetFields(); setBatchProgress(p => ({ ...p, status: 'idle' })); }}>关闭</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情抽屉 —— 显示所有字段 + JSON */}
      <Drawer
        title={detailRecord ? `${detailRecord.emoji || ''} ${detailRecord.name} · ${detailRecord.code}` : '详情'}
        open={detailVisible}
        width={720}
        onClose={() => setDetailVisible(false)}
      >
        {detailRecord && (
          <Descriptions column={2} size="small" bordered labelStyle={{ width: 110 }}>
            <Descriptions.Item label="ID">{detailRecord.id}</Descriptions.Item>
            <Descriptions.Item label="编号">{detailRecord.code}</Descriptions.Item>
            <Descriptions.Item label="species_id">{detailRecord.speciesId || '—'}</Descriptions.Item>
            <Descriptions.Item label="名称">{detailRecord.name}</Descriptions.Item>
            <Descriptions.Item label="emoji">{detailRecord.emoji || '—'}</Descriptions.Item>
            <Descriptions.Item label="稀有度">{renderLevel(detailRecord.level)}</Descriptions.Item>
            <Descriptions.Item label="类别">{detailRecord.beastClass}</Descriptions.Item>
            <Descriptions.Item label="type 标记">{detailRecord.beastType || '—'}</Descriptions.Item>
            <Descriptions.Item label="基础 HP">{detailRecord.baseHp}</Descriptions.Item>
            <Descriptions.Item label="基础攻击">{detailRecord.baseAttack}</Descriptions.Item>
            <Descriptions.Item label="基础防御">{detailRecord.baseDefense}</Descriptions.Item>
            <Descriptions.Item label="基础速度">{detailRecord.baseSpeed}</Descriptions.Item>
            <Descriptions.Item label="睡眠分钟">{fmtNum(detailRecord.sleepDurationMinutes)}</Descriptions.Item>
            <Descriptions.Item label="探索分钟">{fmtNum(detailRecord.exploreDurationMinutes)}</Descriptions.Item>
            <Descriptions.Item label="偏好地形">{detailRecord.preferredTerrain || '—'}</Descriptions.Item>
            <Descriptions.Item label="饱食流失倍率">{fmtNum(detailRecord.satietyDrainMultiplier)}</Descriptions.Item>
            <Descriptions.Item label="森林">{fmtNum(detailRecord.terrainForest)}</Descriptions.Item>
            <Descriptions.Item label="沼泽">{fmtNum(detailRecord.terrainSwamp)}</Descriptions.Item>
            <Descriptions.Item label="沙漠">{fmtNum(detailRecord.terrainDesert)}</Descriptions.Item>
            <Descriptions.Item label="海岸">{fmtNum(detailRecord.terrainCoast)}</Descriptions.Item>
            <Descriptions.Item label="火山">{fmtNum(detailRecord.terrainVolcanic)}</Descriptions.Item>
            <Descriptions.Item label="主食">{detailRecord.primaryFood || '—'}</Descriptions.Item>
            <Descriptions.Item label="体型">{detailRecord.bodySize || '—'}</Descriptions.Item>
            <Descriptions.Item label="实例数">{detailRecord.instanceCount ?? 0}</Descriptions.Item>
            <Descriptions.Item label="图片 baby" span={2}>{detailRecord.textureBaby || '—'}</Descriptions.Item>
            <Descriptions.Item label="图片 juvenile" span={2}>{detailRecord.textureJuvenile || '—'}</Descriptions.Item>
            <Descriptions.Item label="图片 adult" span={2}>{detailRecord.textureAdult || '—'}</Descriptions.Item>
            <Descriptions.Item label="图片 beast" span={2}>{detailRecord.textureBeast || '—'}</Descriptions.Item>
            <Descriptions.Item label="描述" span={2}>
              <div style={{ whiteSpace: 'pre-wrap' }}>{detailRecord.description || '—'}</div>
            </Descriptions.Item>
            <Descriptions.Item label="talent_range" span={2}>{renderJson(detailRecord.talentRange)}</Descriptions.Item>
            <Descriptions.Item label="terrain_talent_range" span={2}>{renderJson(detailRecord.terrainTalentRange)}</Descriptions.Item>
            <Descriptions.Item label="combat_loot" span={2}>{renderJson(detailRecord.combatLoot)}</Descriptions.Item>
            <Descriptions.Item label="growth_stages" span={2}>{renderJson(detailRecord.growthStages)}</Descriptions.Item>
            <Descriptions.Item label="hatch_pool" span={2}>{renderJson(detailRecord.hatchPool)}</Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </Card>
  );
};

export default BeastTab;
